import { useMemo } from 'react';

import { useProductListContext } from '../../context/static/ProductListContext';
import { useStaticContext } from '../../context/static/StaticContext';

interface UseAlgoliaFiltersProps {
  /**
   * Extra clauses placed between the static and the category filters, e.g. `styleCode:D20071`
   */
  additionalFilters?: string[];
  /**
   * Whether to scope the query to the current category. Pass `false` wherever the category
   * details do not describe the listed products, like the search overlay and SRP
   */
  includeCategoryFilter?: boolean;
}

/**
 * Build the Algolia `filters` query from the site's `staticFiltersQuery`, the additional
 * filters and, when `includeCategoryFilter` is set, the current category's
 * `categoryQueryFilter` and `category:<categoryCode>`. Clauses are joined with `AND` in that
 * order and empty ones are skipped.
 *
 * SRP also carries category details (from `/shop/all`, for its SAP CX facets and sorting), so
 * its callers must opt out of the category filter. Otherwise the SRP results are scoped to that
 * category, e.g. `(NOT _tags:hasDiscount) AND category:shop` on production, and no longer match
 * the search overlay.
 *
 * @returns The `filters` string for an Algolia request, or an empty string when none apply
 */
export const useAlgoliaFilters = ({
  additionalFilters = [],
  includeCategoryFilter = true,
}: UseAlgoliaFiltersProps = {}) => {
  const { categoryDetails: { categoryCode, categoryQueryFilter } = {} } = useProductListContext();
  const { configuration: { staticFiltersQuery } = {} } = useStaticContext();

  const categoryFilters = useMemo(() => {
    const filters = [];

    if (includeCategoryFilter) {
      if (categoryQueryFilter) {
        filters.push(categoryQueryFilter);
      }

      if (categoryCode) {
        filters.push(`category:${categoryCode}`);
      }
    }

    return filters;
  }, [categoryCode, categoryQueryFilter, includeCategoryFilter]);

  const filters = useMemo(
    () =>
      [staticFiltersQuery, ...additionalFilters, ...categoryFilters].filter(Boolean).join(' AND '),
    [additionalFilters, categoryFilters, staticFiltersQuery]
  );

  return filters;
};
